import { Accordion, AccordionSummary, Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, OutlinedInput, Select, Stack } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ParentScreenCtx } from "../../../../pages/workbench_build/WorkbenchBuild";
import { eachDeep } from "deepdash-es/standalone";
import { cloneDeep, debounce, get, isArray, isEqual, min, set } from "lodash";
import { treeActions } from "../../../../redux/slices/treeSlice";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { genKey, validateWorkflowItemName } from "../../../../utils/utilFunctions";
import Tree from "rc-tree";
import ErrorModal from "../../Forms/FieldDataValidation/ErrorModal";
import LightTooltip from "../../../../commonComponents/LightTooltip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LabeledDropDown from "../../../TemplateProperties/TPDropDown";
import { api } from "../../../../redux/api";
import InboundConditionControls from "./InboundConditionControls";
import OutboundConditionControls from "./OutboundConditionControls";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

export default function TranslateDataProperties() {
  //default vars
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree, leftTree } = useSelector(state => state.treeData[parentScreen]);
  const selectedNode = rightTree.tree.body.selectedNode;
  const { translatedData } = rightTree.tree.body;
  const { account } = useSelector(state => state.queryParams);

  const inboundTemplatesTree = useSelector(
    (state) => state.treeData.inbound.leftTree.tree.body.translatedData
  );
  
  const outboundTemplatesTree = useSelector(
    (state) => state.treeData.outbound.leftTree.tree.body.translatedData
  );

  //main save function. clone data, find node in tdata, change obj, dispatch new tdata and selnode
  const submitChange = (key, value, bulk, removeValues) => {
    if (!key && !bulk) return;
    // Clone and deeply iterate through the tree data to update the relevant node.
    const updatedData = eachDeep(cloneDeep(translatedData), (obj) => {
      if (obj?.key !== selectedNode?.key) return;

      if (bulk) {
        bulk.forEach((item) => {
          if (item.value === null) {
            if (obj?.hasOwnProperty(item.key)) delete obj[item.key];
          } else {
            obj[item.key] = item.value;
          }
        });
      } else {
        if (key === "NAME") {
          obj.title = value;
          obj[key] = value;
        } else {
          // Update other properties directly on the object.
          if (value === null) {
            if (obj?.hasOwnProperty(key)) delete obj[key];
          } else {
            obj[key] = value;
          }
        }
      }

      // Dispatch an action to update the selected node in the state.
      dispatch(
        treeActions.setRightTreeSelectedNode({
          screen: parentScreen,
          type: rightTree.currentTab,
          data: obj,
        })
      );

      // If the object has children, return them for further iteration.
      if (isArray(obj?.children)) {
        return obj.children;
      }
    });

    // Dispatch an action to update the translated tree data in the state.
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData,
      })
    );
  };

  const debounceChangeHandler = useCallback(debounce(submitChange, 100), [rightTree.tree]);


  //*** "Name" - NAME - text field ***//
  const defaultName = selectedNode?.NAME || "";
  const [name, setName] = useState(defaultName);
  
  // Name validation states
  const [nameErrorModal, setNameErrorModal] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState('');

  //ID string, displayed on the NAME field
  const wfItemId = selectedNode?.ID;

  // Handle changes to the data name, specific logic for onChange event
  const handleNameChange = (e) => {
    if (e?.target?.value || e?.target?.value === '') {
      setName(e.target.value);
    }
  }

  // Handle changes to the data name, specific logic for onBlur event
  const handleNameBlur = (eVal, e) => {
    const oldName = selectedNode?.NAME || '';
    
    const validationResult = validateWorkflowItemName(
      eVal, 
      oldName, 
      translatedData, 
      selectedNode?.key, 
      'TRANSLATE'
    );
    
    if (validationResult.isValid) {
      const newName = eVal?.trim() || '';
      setName(newName);
      if (newName !== oldName) {
        // Mark template as changed immediately to ensure flag is set before tab switch
        dispatch(treeActions.setTemplateChanged({ screen: parentScreen, data: true }));
        submitChange('NAME', newName);
        // Wait for debounce delay + buffer before dispatching
        setTimeout(() => {
          dispatch(treeActions.setIsInputFocusedFalse());
        }, 200);
      } else {
        // If no change, dispatch immediately
        setTimeout(() => {
          dispatch(treeActions.setIsInputFocusedFalse());
        }, 100);
      }
    } else {
      if (validationResult.errorMessage) {
        setNameErrorMessage(t(validationResult.errorMessage));
        setNameErrorModal(true);
      } else if (validationResult.shouldRevert) {
        setName(oldName);
      }
      // For error cases, dispatch immediately
      setTimeout(() => {
        dispatch(treeActions.setIsInputFocusedFalse());
      }, 100);
    }
  }


  //*** "Do not Log Workflow Item Details" - LOG_WFITEM_DETAILS - checkbox ***//
  const defaultLogWorkflowChecked = selectedNode?.LOG_WFITEM_DETAILS === "FALSE" ? true : false;
  const [logWorkflowChecked, setLogWorkflowChecked] = useState(defaultLogWorkflowChecked);

  const handleLogWorkflowChange = (e) => {
    const checked = e?.target?.checked;
    setLogWorkflowChecked(checked);
    debounceChangeHandler('LOG_WFITEM_DETAILS', checked ? 'FALSE' : 'TRUE');
  }


  //*** "Run Concurrently" - CONCURRENT - checkbox - hidden until a show case is found***//
  const defaultConcurrentChecked = selectedNode?.CONCURRENT === "FALSE" ? false : true;
  const [concurrentChecked, setConcurrentChecked] = useState(defaultConcurrentChecked);

  const handleConcurrentChange = (e) => {
    const checked = e?.target?.checked;
    setConcurrentChecked(checked);
    debounceChangeHandler('CONCURRENT', checked ? 'TRUE' : 'FALSE');
  }


  //*** "Disable Workflow Item" - ENABLED - checkbox ***//
  const defaultEnabledChecked = selectedNode?.ENABLED === "TRUE" ? false : true;
  const [enabledChecked, setEnabledChecked] = useState(defaultEnabledChecked);

  const handleEnabledChange = (e) => {
    const checked = e?.target?.checked;
    setEnabledChecked(checked);
    debounceChangeHandler('ENABLED', checked ? 'FALSE' : 'TRUE');
  }

  const handleNameErrorModal = (isOpen) => {
    setNameErrorModal(isOpen);
    if (!isOpen) {
      const oldName = selectedNode?.NAME || '';
      setName(oldName);
    }
  }


  //*** "Get Data to Use" - dropdown + "ID_" - text field ***//

  function getNodesBeforeCurrentNode() {
    if (rightTree?.tree?.body?.translatedData?.[0]?.children?.length === 0) return [
      {
        name: t("EMPTY_COMBO"),
        id: "null",
      }
    ];

    let flatNodeArr = [
      {
        name: t("SELECT"),
        id: "null",
      }
    ];
    let stopLoop = false;

    function loopChildren(childArr) {
      childArr?.forEach((node) => {
        if (stopLoop) return;
        if (node?.key === selectedNode?.key) {
          stopLoop = true;
          return;
        }
        if (node?.ACTION === 'GET_DATA') flatNodeArr.push({
          name: node.NAME,
          id: node.ID,
        });
        if (node.children?.length > 0) {
          loopChildren(node.children);
        }
      })
    }
    loopChildren(rightTree?.tree?.body?.translatedData?.[0]?.children);

    if (flatNodeArr.length === 1) flatNodeArr = [{
      name: t("EMPTY_COMBO"),
      id: "null",
    }]

    return flatNodeArr;
  }

  const getDataEncodingById = (getDataId) => {
    let encodingValue = null;
    function loopChildren(childArr) {
      childArr.forEach((node) => {
        if (encodingValue !== null) return;
        if (node?.ACTION === 'GET_DATA' && node?.ID === getDataId) {
          encodingValue = node?.INBOUND?.ENCODING || node?.ENCODING;
          return;
        }
        if (node?.children?.length > 0) {
          loopChildren(node.children);
        }
      })
    }
    if (rightTree?.tree?.body?.translatedData?.[0]?.children) loopChildren(rightTree?.tree?.body?.translatedData?.[0]?.children);
    return encodingValue;
  }

  const getDataOptions = getNodesBeforeCurrentNode().map(node => {
    return (
      <MenuItem key={node.name} value={node.name} id={node.id}>
        {['EMPTY_COMBO', 'SELECT'].includes(node.name) ? t(node.name) : node.name}
      </MenuItem>
    );
  });

  //TODO: need to make a fuction that gets all workflow nodes in flat ordered array.
  // then for the dropdown, only show Get Data nodes that come before the current translate node in the array
  const defaultGetDataToUseVal = [t('EMPTY_COMBO'), t('SELECT')].includes(selectedNode?.INBOUND_NAME) ? getDataOptions[0]?.props?.value : selectedNode?.INBOUND_NAME || t('EMPTY_COMBO');
  const [getDataToUseVal, setGetDataToUseVal] = useState(defaultGetDataToUseVal);
  const [getDataId, setGetDataId] = useState(selectedNode?.INBOUND_ID || 'null');

  const getDataEncoding = getDataEncodingById(getDataId);
  // const getDataId = 'INBOUND_ID';

  const getDataChange = (e) => {
    if (!e?.target?.value) return;
    setGetDataToUseVal(e.target.value);
    setGetDataId(getDataOptions.find((opt) => opt.props.value === e.target.value)?.props.id);

    if (e.target.value === t('SELECT')) {
      debounceChangeHandler(true, true, [
        { key: 'INBOUND_ID', value: null },
        { key: 'INBOUND_NAME', value: null }
      ])
    } else if (e.target.value === t('EMPTY_COMBO')) {
      debounceChangeHandler(true, true, [
        { key: 'INBOUND_ID', value: null },
        { key: 'INBOUND_NAME', value: t('EMPTY_COMBO') }
      ])
    } else {
      debounceChangeHandler(true, true, [
        { key: 'INBOUND_ID', value: getDataOptions.find((opt) => opt.props.value === e.target.value)?.props.id },
        { key: 'INBOUND_NAME', value: e.target.value }
      ])
    }
  }


  //*** "Use Custom Character Mappings" - CUSTOM_CHAR_MAPPING - checkbox ***//
  const defaultCustomCharChecked = selectedNode?.CUSTOM_CHAR_MAPPING === "TRUE" ? true : false;
  const [customCharChecked, setCustomCharChecked] = useState(defaultCustomCharChecked);

  const handleCustomCharChange = (e) => {
    const checked = e?.target?.checked;
    setCustomCharChecked(checked);
    debounceChangeHandler('CUSTOM_CHAR_MAPPING', checked ? 'TRUE' : 'FALSE');
  }


  //*** Template Conditions Tree ***//


  //!!! TODO:
  //bind condition data to rcTree nodes
  //either add drag/drop functionality, or use arrow buttons (like SWT) to change node position
  //create inbound and outbound conditionally rendered components, using selected tree node data
  //ui for controls
  //data saving logic for controls
  //save logic for translated data node's CONDITIONAL_TEMPLATE property


  const treeRef = useRef();
  const treeContainerRef = useRef();

  //take input of CONDITIONAL_TEMPLATE array, convert to rcTree data
  const convertNodeDataToRcTree = (array) => {
    if (!array) return [];

    let convertedTreeData = [];

    array.forEach((inboundNode, inboundIndex) => {
      let newNode = {
        // title: inboundNode.NAME,
        key: `${genKey()}-${inboundIndex}`,
        type: "inbound",
        children: [],
        ...inboundNode,
      }

      if (inboundNode.OUTBOUND_CONDITION) {
        inboundNode.OUTBOUND_CONDITION.forEach((outboundNode, outboundIndex) => {
          newNode.children.push({
            // title: outboundNode.NAME,
            key: `${genKey()}-${outboundIndex}`,
            type: "outbound",
            children: [],
            ...outboundNode,
          });
        });
      }
      if (newNode.OUTBOUND_CONDITION) delete newNode.OUTBOUND_CONDITION;

      convertedTreeData.push(newNode);
    });

    return convertedTreeData;
  }

  //take input of rcTree data, convert to CONDITIONL_TEMPLATE array
  const convertRcTreeDataToNodeData = (treeArr) => {
    let convertedTreeData = [];

    treeArr.forEach((inboundNode, inboundIndex) => {
      let newNode = { ...inboundNode };
      // delete newNode.title;
      delete newNode.children;
      delete newNode.type;
      delete newNode.key;
      delete newNode.tval;
      delete newNode.tgval;

      let newOutboundConditions = [];

      inboundNode.children.forEach((outboundNode, outboundIndex) => {
        let newObNode = { ...outboundNode };
        // delete newObNode.title;
        delete newObNode.children;
        delete newObNode.type;
        delete newObNode.key;
        delete newObNode.tval;
        delete newObNode.tgval;

        newOutboundConditions.push(newObNode);
      });

      newNode.OUTBOUND_CONDITION = newOutboundConditions;

      convertedTreeData.push(newNode);
    })

    return convertedTreeData;
  }

  const treeDataDefault = convertNodeDataToRcTree(selectedNode.CONDITIONAL_TEMPLATE);
  const defaultExpandedKeys = treeDataDefault.map((node) => node.key);

  const [treeData, setTreeData] = useState(treeDataDefault);
  const [treeSelectedKeys, setTreeSelectedKeys] = useState(treeData?.length > 0 ? [treeData[0].key] : []);
  const [treeSelectedNode, setTreeSelectedNode] = useState(treeData?.length > 0 ? treeData[0] : false);
  const [treeExpandedKeys, setTreeExpandedKeys] = useState(defaultExpandedKeys);
  const [infoRerender, setInfoRerender] = useState(true);//using this state to force rerender of IB/OB info components, every time new node selected

  const treeDataReverted = convertRcTreeDataToNodeData(treeData);

  const onTreeSelect = (key, node) => {
    setTreeSelectedKeys(key);
    if (node?.node) setTreeSelectedNode(node?.node);
    if (treeSelectedNode?.key !== node?.node?.key) setInfoRerender(false);
  }

  useEffect(() => {
    if (!infoRerender) setInfoRerender(true);
  }, [infoRerender]);

  const deleteTreeNodeByKey = (givenKey, node, type) => {

    if (node?.type === "inbound") {
      const newTreeData = treeData.filter((treeNode) => treeNode.key !== givenKey);
      if (newTreeData) {
        setTreeData(newTreeData);
        if (newTreeData.length) {
          //if deleted node is inbound, set selected node to first inbound node

          setTimeout(() => {
            setTreeSelectedKeys([newTreeData[0].key]);
            setTreeSelectedNode(newTreeData[0]);
          }, 100);
        } else {
          //if no more inbound nodes, clear selected node
          setTimeout(() => {
            setTreeSelectedKeys([]);
            setTreeSelectedNode(false);
          }, 100);
        }
      }
    } else if (node?.type === "outbound") {
      //if node is outbound, set selected node to parent inbound node
      let parentNode = false;
      const newTreeData = eachDeep(cloneDeep(treeData), (obj) => {
        //if obj.children contains givenKey, log the key for selection after loop
        if (obj?.children?.find((child) => child.key === givenKey)) {
          parentNode = { ...obj };
          obj.children = obj.children.filter((child) => child.key !== givenKey);
        }

        // If the object has children, return them for further iteration.
        if (isArray(obj?.children)) {
          return obj.children;
        }
      });

      setTreeData(newTreeData);

      if (newTreeData.length) {
        if (parentNode) {
          //if inbound nodes exist, and parentNode exists, select parent's key.
          setTimeout(() => {
            setTreeSelectedKeys([parentNode.key]);
            setTreeSelectedNode(parentNode);
          }, 100);
        } else if (newTreeData[0]) {
          //if inbound nodes exist, but parentNode does not exist, select first inbound node
          setTimeout(() => {
            setTreeSelectedKeys([newTreeData[0].key]);
            setTreeSelectedNode(newTreeData[0]);
          }, 100);
        }
      } else {
        //if no more inbound nodes, clear selected node
        setTimeout(() => {
          setTreeSelectedKeys([]);
          setTreeSelectedNode(false);
        }, 100);
      }
    }
  }

  const treeIconRenderer = (node) => {
    return <span className={node?.data?.type === "inbound" ? "GET_DATA" : "SEND_DATA"}></span>
  }

  // Helper function to check if template exists in template trees
  const checkTemplateExists = (node) => {
    const templateWfGroup = node?.TEMPLATE_LOCATION?.split("/")?.[1];
    const templateName = node?.TEMPLATE?.endsWith('.json') ? node?.TEMPLATE?.slice(0, -5) : node?.TEMPLATE;
    
    if (!templateWfGroup || !templateName) {
      return false;
    }
    
    if (node.type === "inbound") {
      // Check inbound template existence
      if (!inboundTemplatesTree || !Array.isArray(inboundTemplatesTree)) {
        return false;
      }
      
      // Find the workflow group
      const workflowGroup = inboundTemplatesTree.find(treeNode => 
        treeNode?.type === "workflowGroup" && treeNode?.title === templateWfGroup
      );
      
      if (!workflowGroup || !workflowGroup.children) {
        return false;
      }
      
      // Check if template exists in the workflow group's children
      const templateExists = workflowGroup.children.some(childNode => 
        childNode?.title === templateName || childNode?.title === `${templateName}.json`
      );
      
      return templateExists;
    } else if (node.type === "outbound") {
      // Check outbound template existence
      if (!outboundTemplatesTree || !Array.isArray(outboundTemplatesTree)) {
        return false;
      }
      
      // Find the workflow group
      const workflowGroup = outboundTemplatesTree.find(treeNode => 
        treeNode?.type === "workflowGroup" && treeNode?.title === templateWfGroup
      );
      
      if (!workflowGroup || !workflowGroup.children) {
        return false;
      }
      
      // Check if template exists in the workflow group's children
      const templateExists = workflowGroup.children.some(childNode => 
        childNode?.title === templateName || childNode?.title === `${templateName}.json`
      );
      
      return templateExists;
    }
    
    return false;
  };

  const treeTitleRender = (node) => {
    
    //add outbound condition to current selected inbound condition
    const handleAddObClick = () => {
      const newKey = `${genKey()}-${treeData.length}` + '';
      //default properties for a new outbound condition
      const newNodeData = {
        // title: "UNNAMED",
        key: newKey,
        type: "outbound",
        children: [],
        OUTBOUND_NAME: "UNNAMED",
        NAME: "UNNAMED",
        TEMPLATE_LOCATION: `${account}/${leftTree.tree.body.selectedNode.workflowGroup}/outbound_templates`,
        TEMPLATE: "",
        CONDITION: []
      }

      const newTreeData = eachDeep(cloneDeep(treeData), (obj) => {
        if (obj?.key !== node?.key) return;
        obj.children?.unshift(newNodeData);

        // If the object has children, return them for further iteration.
        if (isArray(obj?.children)) {
          return obj.children;
        }
      });

      setTreeData(newTreeData);

      //timeout triggers after onTreeSelect trigger is executed. clicking on the icon also clicks the row, which triggers onTreeSelect
      setTimeout(() => {
        setTreeSelectedKeys([newKey]);
        setTreeSelectedNode(newNodeData);
        if (!treeExpandedKeys.includes(node?.key)) setTreeExpandedKeys((prev) => [...prev, node?.key]);
      }, 100);
    }

    const handleDeleteClick = (e) => {
      e.stopPropagation();
      deleteTreeNodeByKey(node.key, node);
    }

    const iconSize = "20px";

    //logic for title render...
    const wfGroup = node?.TEMPLATE_LOCATION ? node.TEMPLATE_LOCATION.split("/")[1] : ''
    let titleDisplayText = `${node.type === "inbound" ? node.NAME : node.OUTBOUND_NAME}`;
    if (node.type === "inbound" && !node?.NAME) titleDisplayText = 'UNNAMED';
    if (node.type === "outbound" && !node?.OUTBOUND_NAME) titleDisplayText = 'UNNAMED';

    if (node?.TEMPLATE_LOCATION) titleDisplayText += ` (${t('COL_GROUP')}:${wfGroup}/${t('TEMPLATE')}:`;
    if (node?.TEMPLATE) {
      const templateExists = checkTemplateExists(node);
      const templateName = node.TEMPLATE.endsWith('.json') ? node.TEMPLATE.slice(0, -5) : node.TEMPLATE;
      titleDisplayText += `${templateName}`;
      if (!templateExists) {
        titleDisplayText += ` - ${t('LBL_SEL_TEMPLATE_MISSING')}`;
      }
    }
    if (node?.TEMPLATE_LOCATION) titleDisplayText += `)`;



    return (
      <span
  data-key={node.key}
  title={titleDisplayText}
  className="full-width-tree-node-row"
  style={{ display: "block", width: "100%" }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: node.type === "inbound" ? "calc(100% - 62px)" : "calc(100% - 35px)",
      overflow: "hidden",
    }}
  >
    <span
      style={{
        flex: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        minWidth: 0, // needed when flex: 1 is used
      }}
    >
      {titleDisplayText}
    </span>

    <div
      style={{
        right: "0px",
        display: "flex",
        paddingLeft: "8px",
        paddingRight: "6px",
        position: "absolute",
        alignItems: "center",
        height: "20px",
      }}
    >
      {node.type === "inbound" && (
        <LightTooltip
          title={t('ADD_OUTBOUND_CONDITION')}
          arrow
          placement="left"
        >
          <div
            style={{
              cursor: "pointer",
              height: "20px",
              borderRadius: "5px",
              padding: "0 5px",
              marginRight: "0px",
              display: "flex",
              alignItems: "center",
            }}
            className="bg-color-on-hover-dark"
            onClick={handleAddObClick}
          >
            <AddOutlinedIcon style={{ color: "#666", fontSize: iconSize }} />
          </div>
        </LightTooltip>
      )}
      <LightTooltip title={t("DELETE")} arrow placement="right">
        <div
          className="bg-color-on-hover-dark DELETE_MUI"
          style={{
            cursor: "pointer",
            height: iconSize,
            width: iconSize,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "5px",
          }}
          onClick={handleDeleteClick}
        />
      </LightTooltip>
    </div>
  </div>
</span>


    );
    
  }

  const switcherIcon = (obj) => {
    if (obj.isLeaf) return;
    if (obj.expanded) return <ArrowDropDownIcon />;
    else return <ArrowRightIcon sx={{ color: "#666666" }} />;
  };

  const onAddClick = () => {
    const newTreeData = [...treeData];
    const newKey = `${genKey()}-${newTreeData.length}`;
    //default properties for a new inbound condition
    let newNodeData = {

      // title: "UNNAMED",
      key: newKey,
      type: "inbound",
      NAME: "UNNAMED",
      FIELDNUM: "0",
      TEMPLATE_LOCATION: `${account}/${leftTree.tree.body.selectedNode.workflowGroup}/inbound_templates`,
      TEMPLATE: "", // Add missing TEMPLATE property
      RECORD_NUMBER: "0",
      DEFAULT: "FALSE",
      WF_ITEM_DEPENDENCY_TIMEOUT: "5000",
      COMPARE_ACTION_TRIM: "FALSE",
      COMPARE_ACTION_IGNORECASE: "FALSE",
      COMPARE_ACTION_NOT: "FALSE",
      OUTBOUND_CONDITION: [
        {
          OUTBOUND_NAME: "UNNAMED",
          NAME: "UNNAMED",
          TEMPLATE_LOCATION: `${account}/${leftTree.tree.body.selectedNode.workflowGroup}/outbound_templates`,
          TEMPLATE: "",
          CONDITION: []
        }
      ],
      children: [
        {
          title: "UNNAMED",
          key: `${genKey()}-${newTreeData.length}-0`,
          type: "outbound",
          children: [],
          OUTBOUND_NAME: "UNNAMED",
          TEMPLATE_LOCATION: `${account}/${leftTree.tree.body.selectedNode.workflowGroup}/outbound_templates`,
          TEMPLATE: "",
          CONDITION: []
        },
      ],
    }

    newTreeData.push(newNodeData);
    setTreeData(newTreeData);
    setTreeSelectedKeys([newKey]);
    setTreeSelectedNode(newNodeData);
    if (!treeExpandedKeys.includes(newKey)) setTreeExpandedKeys((prev) => [...prev, newKey]);

    //scroll to bottom. timeout is needed to allow the tree to update before scrolling
    setTimeout(() => {
      treeContainerRef.current.scrollTop = treeContainerRef.current.scrollHeight;
    }, 100);
  }

  const onUpClick = () => {
    if (treeSelectedNode.type === "inbound") {
      //find the inbound node and move it up in the array. if at top, do nothing
      const newTreeData = [...treeData];
      const nodeIndex = newTreeData.findIndex((node) => node.key === treeSelectedNode.key);
      if (nodeIndex === 0) return;
      const tempNode = newTreeData[nodeIndex];
      newTreeData[nodeIndex] = newTreeData[nodeIndex - 1];
      newTreeData[nodeIndex - 1] = tempNode;
      setTreeData(newTreeData);
    } else if (treeSelectedNode.type === "outbound") {
      //find the outbound node that is nested inside an inbound node and move it up in the array. if at top, do nothing
      const parentNode = treeData.find((node) => node.children.find((child) => child.key === treeSelectedNode.key));
      const newTreeData = [...treeData];
      const nodeIndex = parentNode.children.findIndex((node) => node.key === treeSelectedNode.key);
      if (nodeIndex === 0) return;
      const tempNode = parentNode.children[nodeIndex];
      parentNode.children[nodeIndex] = parentNode.children[nodeIndex - 1];
      parentNode.children[nodeIndex - 1] = tempNode;
      setTreeData(newTreeData);
    }
  }

  const onDownClick = () => {
    if (treeSelectedNode.type === "inbound") {
      //find the inbound node and move it down in the array. if at bottom, do nothing
      const newTreeData = [...treeData];
      const nodeIndex = newTreeData.findIndex((node) => node.key === treeSelectedNode.key);
      if (nodeIndex === newTreeData.length - 1) return;
      const tempNode = newTreeData[nodeIndex];
      newTreeData[nodeIndex] = newTreeData[nodeIndex + 1];
      newTreeData[nodeIndex + 1] = tempNode;
      setTreeData(newTreeData);
    } else if (treeSelectedNode.type === "outbound") {
      //find the outbound node that is nested inside an inbound node and move it down in the array. if at bottom, do nothing
      const parentNode = treeData.find((node) => node.children.find((child) => child.key === treeSelectedNode.key));
      const newTreeData = [...treeData];
      const nodeIndex = parentNode.children.findIndex((node) => node.key === treeSelectedNode.key);
      if (nodeIndex === parentNode.children.length - 1) return;
      const tempNode = parentNode.children[nodeIndex];
      parentNode.children[nodeIndex] = parentNode.children[nodeIndex + 1];
      parentNode.children[nodeIndex + 1] = tempNode;
      setTreeData(newTreeData);
    }
  }

  const handleTreeNodeExpand = (expandedKeys) => {
    setTreeExpandedKeys(expandedKeys)
  }

  const treeDataFlatArr = [];

  treeData.forEach((node) => {
    treeDataFlatArr.push(node);
    if (node.children?.length) {
      node.children.forEach((child) => {
        treeDataFlatArr.push(child);
      });
    }
  });


  useEffect(() => {
    // when treeData is changed, check for diff. if diff, submit change to the node in redux
    if (!isEqual(treeDataReverted, selectedNode.CONDITIONAL_TEMPLATE)) {
      submitChange('CONDITIONAL_TEMPLATE', treeDataReverted);
    }
  }, [treeData]);

  useEffect(() => {
    // console.log("treeSelectedNode", treeSelectedNode);
    // when selected node changes, set the selected keys to the new node's key
    if (treeSelectedNode) {
      setTreeSelectedKeys([treeSelectedNode.key]);
    }
  }, [treeSelectedNode]);

  return (
    <div>

      {/* "Name" text field + ID number */}
      <FormControl variant='outlined' className='width-100' size='small'>
        <InputLabel
          htmlFor='translated-data-name-input'
          sx={{ padding: "0 6px !important", left: "-5px" }}
        >
          {t('COL_NAME')}
        </InputLabel>
        <OutlinedInput
          id="translated-data-name-input"
          size='small'
          variant='outlined'
          fullWidth
          autoComplete='off'
          value={name}
          endAdornment={<div style={styles.getDataNameAdornment}>{`ID: ${wfItemId}`}</div>}
          sx={{ justifyContent: "flex-end" }}
          onChange={(e) => handleNameChange(e)}
          onBlur={(e) => handleNameBlur(e?.target?.value, e)}
          onFocus={() => dispatch(treeActions.setIsInputFocusedTrue())}
        />
      </FormControl>

      {/* "Do not Log Workflow Item Details" checkbox */}
      <FormControl variant='outlined' className='width-100' size='small'>
        <FormControlLabel
          control={
            <Checkbox
              checked={logWorkflowChecked}
              onChange={handleLogWorkflowChange}
              name='logWorkflowChecked'
              color='primary'
            />
          }
          label={t('LBL_LOG_NO_WFITEM_DETAILS')}
        />
      </FormControl>

      {/* "Run Concurrently" checkbox - hidden until show case is found*/}
      {false && (
        <FormControl variant='outlined' className='width-100' size='small'>
          <FormControlLabel
            control={
              <Checkbox
                checked={concurrentChecked}
                onChange={handleConcurrentChange}
                name='concurrentChecked'
                color='primary'
              />
            }
            label={t('LBL_RUN_CONCURRENTLY')}
          />
        </FormControl>
      )}


      {/* "Disable Workflow Item" checkbox */}
      <FormControl variant='outlined' className='width-100' size='small'>
        <FormControlLabel
          control={
            <Checkbox
              checked={enabledChecked}
              onChange={handleEnabledChange}
              name='enabledChecked'
              color='primary'
            />
          }
          label={t('LBL_DISABLE_WFITEM')}
        />
      </FormControl>

      {/* 'Get Data' to use - dropdown + ID - INBOUND_ID - text*/}
      <FormControl variant="outlined" size="small" style={{ minWidth: '150px', width: '100%', marginTop: '10px' }}>
        <InputLabel>{t("LBL_GET_DATA_TO_USE").replace(":", "")}</InputLabel>
        <Select
          size='small'
          // className='width-100'
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 300
              }
            }
          }}
          value={getDataToUseVal}
          onChange={getDataChange}
          endAdornment={<span style={styles.getDataNameAdornment}>{t('ID_') + ': ' + getDataId}</span>}
        >
          {getDataOptions}
        </Select>
      </FormControl>
      
      {(getDataToUseVal !== t('EMPTY_COMBO') && getDataToUseVal !== t('SELECT')) && (
        <>
          {/* Custom Character Mappings - CUSTOM_CHAR_MAPPING - checkbox - hidden for now*/}
          {["IBM-JAPAN", "FUJITSU-JAPAN", "Cp1047"].includes(getDataEncoding) && (
            <FormControl variant='outlined' className='width-100' size='small'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customCharChecked}
                    onChange={handleCustomCharChange}
                    name='customCharChecked'
                    color='primary'
                  />
                }
                label={t('LBL_USE_ENCODING_CONVERSION')}
              />
            </FormControl>
          )}

          {/* Template Conditions tree component */}
          <div style={{ marginTop: "10px" }}>
            <div style={{ ...styles.treeHeaderContainer }}>
              <span style={{ fontSize: "12px", color: "#666" }}>{t('LBL_TEM_CONDITIONS')}</span>
              <div style={{display: "flex"}}>
                <LightTooltip
                  title={treeSelectedNode.type === "outbound" ? t('MOVE_OUTBOUND_CONDITION_UP') : t('MOVE_INBOUND_CONDITION_UP')}
                  placement="top"
                >
                  <div
                    style={{ cursor: 'pointer', borderRadius: "5px", padding: "0 5px", display: "flex", alignItems: "center"}}
                    className="bg-color-on-hover"
                    onClick={onUpClick}
                  >
                    <ArrowUpwardIcon style={{ color: "#666" }} color='#666' />
                  </div>
                </LightTooltip>

                <LightTooltip
                  title={treeSelectedNode.type === "outbound" ? t('MOVE_OUTBOUND_CONDITION_DOWN') : t('MOVE_INBOUND_CONDITION_DOWN')}
                  placement="top"
                >
                  <div
                    style={{ cursor: 'pointer', borderRadius: "5px", padding: "0 5px", display: "flex", alignItems: "center"}}
                    className="bg-color-on-hover"
                    onClick={onDownClick}
                  >
                    <ArrowDownwardIcon style={{ color: "#666" }} color='#666' />
                  </div>
                </LightTooltip>

                <LightTooltip
                  title={t('ADD_INBOUND_CONDITION')}
                  placement="top"
                >
                  <div
                    style={{ cursor: 'pointer', borderRadius: "5px", padding: "0 5px", display: "flex", alignItems: "center", marginRight: treeDataFlatArr.length > 7 ? "24px" : "10px" }}
                    className="bg-color-on-hover"
                    onClick={onAddClick}
                  >
                    <AddOutlinedIcon style={{ color: "#666" }} color='#666' />
                  </div>
                </LightTooltip>
              </div>
            </div>
            <div ref={treeContainerRef} style={styles.treeContainer}>
              <Tree
                ref={treeRef}
                className="td-tc-tree"
                selectedKeys={treeSelectedKeys}
                onSelect={onTreeSelect}
                onExpand={handleTreeNodeExpand}
                expandedKeys={treeExpandedKeys}
                treeData={treeData}
                icon={treeIconRenderer}
                titleRender={treeTitleRender}
                showIcon
                switcherIcon={switcherIcon}
              //TODO: need right click? 
              // onRightClick={(...arg) => {
              //   const callFunction = createThrottledFunction(onRightClick, 1000, ...arg);
              //   callFunction();
              // }} 
              />
            </div>
          </div>

          {/* Inbound Condition Controls */}
          {(treeSelectedNode && treeSelectedNode.type === "inbound" && infoRerender) && (
            <InboundConditionControls
              treeData={treeData}
              setTreeData={setTreeData}
              treeSelectedNode={treeSelectedNode}
              setTreeSelectedNode={setTreeSelectedNode}
            />
          )}

          {/* Outbound Condition Controls */}
          {(treeSelectedNode && treeSelectedNode.type === "outbound" && infoRerender) && (
            <OutboundConditionControls
              treeData={treeData}
              setTreeData={setTreeData}
              treeSelectedNode={treeSelectedNode}
              setTreeSelectedNode={setTreeSelectedNode}
              parentNode={treeData.find((node) => node.children.find((child) => child.key === treeSelectedNode.key))}
            />
          )}
        </>
      )}

      {/* Error Modal for name validation */}
      <ErrorModal
        errorModal={nameErrorModal}
        setErrorModal={setNameErrorModal}
        errorMessage={nameErrorMessage}
        errorType={t('ERROR')}
        handleErrorModal={handleNameErrorModal}
      />

    </div>
  )
}

const styles = {
  getDataNameAdornment: {
    color: "#666666",
    fontSize: "9px",
    position: "absolute",
    right: "13px",
    top: "-6px",
    backgroundColor: "#FFF",
    zIndex: "2",
    padding: "0 5px",
    height: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  treeHeaderContainer: {
    paddingTop: '8px',
    paddingBottom: '2px',
    paddingLeft: '0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  treeContainer: {
    height: '100%',
    width: '100%',
    border: '1px solid #999',
    borderTopLeftRadius: '0px',
    borderTopRightRadius: '0px',
    borderRadius: '4px',
    minHeight: "161px",
    maxHeight: "161px",
    overflowY: "auto"
  },
  fpContainer: {
    // padding: "0 16px 16px 16px"
    padding: "0 4px 4px 4px",
  },
  accordianContainer: {
    border: "none",
    padding: "0",
    margin: "0",
    boxShadow: "none",
    backgroundColor: "unset",
  },
  accordianHeader: {
    border: "none",
    padding: "0",
    margin: "0",
    backgroundColor: "unset",
  },
  accordianContent: {
    border: "none",
    padding: "0 0px",
    margin: "0",
    backgroundColor: "unset",
  },
};